import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { PaymentForm, CreditCard as SquareCreditCard } from "react-square-web-payments-sdk";
import { toast } from "react-toastify";
import axios from "axios";
import { ShieldCheck, X } from "lucide-react";

/* ---------------- PREMIUM STYLES ---------------- */

const premiumSquareStyles = `
@keyframes squareGradientMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.square-gradient-border {
  background-size: 300% 300%;
  animation: squareGradientMove 6s ease infinite;
}

@keyframes squareModalEnter {
  0% { opacity: 0; transform: scale(0.95) translateY(15px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

.square-modal-animate {
  animation: squareModalEnter 0.35s ease forwards;
}
`;

const SquarePaymentPortal = ({ open, onClose, orderId, amount, settings, onSuccess }) => {

  /* ✅ Hooks must always run */
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = premiumSquareStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  /* AFTER hooks */
  if (!open) return null;

  const handlePayment = async (tokenResult) => {
    try {
      const { token } = tokenResult;

      const { data } = await axios.post(`${window.BASE_API_URL}place-order`, {
        orderId,
        amount,
        token,
        live: settings?.squareup_live || 0,
        type: "squareup",
      });

      if (data?.payment?.status === "COMPLETED" || data?.success) {
        toast.success("✅ Payment Successful!");
        onSuccess({ success: true, orderId: data?.id });
        onClose();
      } else {
        toast.error("❌ Square payment failed");
      }
    } catch (err) {
      console.error("Square Payment Error:", err);
      toast.error("Square payment failed");
    }
  };

  const env = settings?.squareup_live === 1 ? "PRODUCTION" : "SANDBOX";

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999999]">
      
      <div className="relative w-[420px] p-[1px] rounded-3xl bg-gradient-to-r from-emerald-500 via-teal-400 to-green-500 square-gradient-border">
        
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl square-modal-animate relative">
          
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg">
              <ShieldCheck size={22} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                Secure Square Payment
              </h3>
              <p className="text-sm text-gray-400">
                100% encrypted & protected
              </p>
            </div>
          </div>

          {/* Payment Form */}
          <PaymentForm
            applicationId={settings?.squareup_app_id}
            locationId={settings?.squareup_location}
            environment={env}
            cardTokenizeResponseReceived={handlePayment}
          >
            <SquareCreditCard
              buttonProps={{
                children: `Pay ₹${amount} Securely`,
                css: {
                  background: "linear-gradient(135deg,#10b981,#14b8a6)",
                  color: "#fff",
                  borderRadius: "14px",
                  fontSize: "16px",
                  fontWeight: "600",
                  padding: "14px",
                  width: "100%",
                  boxShadow: "0 10px 25px rgba(16,185,129,0.4)",
                  transition: "all 0.3s ease",
                },
              }}
            />
          </PaymentForm>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full 
                       bg-gray-100 hover:bg-gray-200 
                       transition duration-300"
          >
            <X size={18} className="text-gray-600" />
          </button>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default SquarePaymentPortal;